import { Injectable, DestroyRef, inject } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class WorkerPoolService {
  private workers: Record<string, Worker[]> = {};
  private destroyRef = inject(DestroyRef);

  constructor() {
    _setInstance(this);

    this.destroyRef.onDestroy(() => {
      this.terminateAll();
      _setInstance(null);
    });
  }

  /**
   * Get a worker from the pool, creating one via the factory if the pool is empty.
   * @param key - A unique identifier for this worker type.
   * @param factory - A function that creates a new Worker instance.
   *   Must use `new Worker(new URL('...', import.meta.url))` so esbuild can
   *   detect and bundle the worker entry point.
   */
  getWorker(key: string, factory: () => Worker): Worker {
    if (!this.workers[key]) {
      this.workers[key] = [];
    }

    if (this.workers[key].length === 0) {
      this.workers[key].push(factory());
    }

    return this.workers[key].pop()!;
  }

  returnWorker(key: string, worker: Worker): void {
    if (!this.workers[key]) {
      this.workers[key] = [];
    }
    this.workers[key].push(worker);
  }

  /** Terminate all pooled workers and clear the pool. */
  terminateAll(): void {
    for (const key of Object.keys(this.workers)) {
      for (const worker of this.workers[key]) {
        worker.terminate();
      }
    }
    this.workers = {};
  }
}

// =============================================================================
// Module-level accessor for non-DI code
// =============================================================================

let _instance: WorkerPoolService | null = null;

function _setInstance(instance: WorkerPoolService | null): void {
  _instance = instance;
}

/**
 * Get the active WorkerPoolService instance.
 */
export function getWorkerPool(): WorkerPoolService {
  if (!_instance) {
    throw new Error('WorkerPoolService not yet initialized');
  }
  return _instance;
}
